export const BUSINESS_SUPPORT_HOME_URL =
  'https://business.facebook.com/business-support-home';

export const RESTRICTION_APPEAL_HINT =
  'Appeal in Meta Business Suite → All tools → Business Support Home. Meta usually decides within 24–48 hours.';

export interface MetaAccountRestriction {
  restriction_type: string;
  expires_at?: string | null;
  remediation?: string | null;
  detected_at?: string | null;
}

type RestrictionSeverity = 'blocking' | 'limited';

interface RestrictionCopy {
  summary: string;
  severity: RestrictionSeverity;
}

export const RESTRICTION_COPY: Record<string, RestrictionCopy> = {
  RESTRICTED_ADD_PHONE_NUMBER_ACTION: {
    summary:
      'You cannot add new phone numbers to this WhatsApp Business account. Numbers already connected keep working.',
    severity: 'limited',
  },
  RESTRICTED_BIZ_INITIATED_MESSAGING: {
    summary:
      'You cannot start conversations with customers. Template messages — campaigns, notifications and automated sends — will not go out; you can still reply inside an open 24-hour customer service window.',
    severity: 'blocking',
  },
  RESTRICTED_CUSTOMER_INITIATED_MESSAGING: {
    summary:
      'You cannot reply to customers who message you. Replies inside the 24-hour customer service window will not send.',
    severity: 'blocking',
  },
  RESTRICTED_UTILITY_TEMPLATES: {
    summary:
      'You cannot send utility templates. Order updates, receipts, appointment reminders and similar notifications will not go out.',
    severity: 'blocking',
  },
  RESTRICTED_DIRECT_SEND_UTILITY_TEMPLATES: {
    summary:
      'You cannot send utility templates on demand. Order updates, receipts and reminders can no longer be sent to customers directly.',
    severity: 'blocking',
  },
  RESTRICTED_BUSINESS_INITIATED_CALLING: {
    summary: 'You cannot place WhatsApp calls to customers.',
    severity: 'limited',
  },
  RESTRICTED_USER_INITIATED_CALLING: {
    summary: 'Customers cannot call you on WhatsApp.',
    severity: 'limited',
  },
  RESTRICTED_BIZ_INITIATED_AND_USER_INITIATED_CALLING: {
    summary:
      'WhatsApp calling is switched off in both directions — you cannot call customers and they cannot call you.',
    severity: 'limited',
  },
  RESTRICTED_USER_INITIATED_CALLING_CALL_BUTTON_HIDDEN: {
    summary:
      'Customers cannot call you on WhatsApp, and the call button is hidden on your profile.',
    severity: 'limited',
  },
};

export const UNKNOWN_RESTRICTION_COPY: RestrictionCopy = {
  summary:
    'Meta has restricted part of this WhatsApp account. Open Business Support Home in Meta Business Suite for the exact details.',
  severity: 'blocking',
};

export type RestrictionExpiryKind = 'none' | 'active' | 'ended' | 'unknown';

export interface RestrictionExpiry {
  kind: RestrictionExpiryKind;
  at: Date | null;
  label: string;
}

function formatInstant(date: Date): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

export function classifyRestrictionExpiry(
  expiresAt: string | null | undefined,
  now: Date = new Date()
): RestrictionExpiry {
  if (expiresAt === null || expiresAt === undefined || expiresAt === '') {
    return { kind: 'none', at: null, label: 'No end date given by Meta' };
  }

  if (typeof expiresAt !== 'string') {
    return { kind: 'unknown', at: null, label: 'End date unavailable' };
  }

  const at = new Date(expiresAt);
  if (Number.isNaN(at.getTime())) {
    return { kind: 'unknown', at: null, label: 'End date unavailable' };
  }

  if (at.getTime() > now.getTime()) {
    return { kind: 'active', at, label: `Lifts ${formatInstant(at)}` };
  }

  return {
    kind: 'ended',
    at,
    label: `Ended ${formatInstant(at)} — waiting for Meta to confirm it lifted`,
  };
}

export interface RestrictionNotice {
  type: string;
  isUnknownType: boolean;
  severity: RestrictionSeverity;
  summary: string;
  expiry: RestrictionExpiry;
  remediation: string | null;
}

function toNotice(
  entry: unknown,
  now: Date
): RestrictionNotice | null {
  if (!entry || typeof entry !== 'object') return null;

  const raw = (entry as MetaAccountRestriction).restriction_type;
  if (typeof raw !== 'string') return null;

  const type = raw.trim();
  if (!type) return null;

  const copy = RESTRICTION_COPY[type.toUpperCase()] ?? UNKNOWN_RESTRICTION_COPY;
  const remediationRaw = (entry as MetaAccountRestriction).remediation;
  const remediation =
    typeof remediationRaw === 'string' && remediationRaw.trim()
      ? // Trimmed of surrounding whitespace only — the text itself is Meta's.
        remediationRaw.trim()
      : null;

  return {
    type,
    isUnknownType: !RESTRICTION_COPY[type.toUpperCase()],
    severity: copy.severity,
    summary: copy.summary,
    expiry: classifyRestrictionExpiry(
      (entry as MetaAccountRestriction).expires_at,
      now
    ),
    remediation,
  };
}

export interface AccountRestrictionsNotice {
  restrictions: RestrictionNotice[];
  tone: 'error' | 'warning';
  headline: string;
  summaryLine: string;
  appeal: { hint: string; url: string; label: string };
}

function accountLabel(
  accountName?: string | null,
  phoneNumber?: string | null
): string {
  if (accountName && phoneNumber) return `${accountName} (${phoneNumber})`;
  return accountName || phoneNumber || 'Your WhatsApp account';
}

export interface BuildAccountRestrictionsNoticeInput {
  restrictions?: unknown;
  accountName?: string | null;
  phoneNumber?: string | null;
  now?: Date;
}

export function buildAccountRestrictionsNotice({
  restrictions,
  accountName,
  phoneNumber,
  now = new Date(),
}: BuildAccountRestrictionsNoticeInput): AccountRestrictionsNotice | null {
  if (!Array.isArray(restrictions) || restrictions.length === 0) return null;

  const notices = restrictions
    .map((entry) => toNotice(entry, now))
    .filter((n): n is RestrictionNotice => n !== null);

  if (notices.length === 0) return null;

  const label = accountLabel(accountName, phoneNumber);
  const blocking = notices.some((n) => n.severity === 'blocking');

  return {
    restrictions: notices,
    tone: blocking ? 'error' : 'warning',
    headline:
      notices.length === 1
        ? `${label} has been restricted by Meta.`
        : `${label} has ${notices.length} restrictions from Meta.`,
    summaryLine: notices.map((n) => n.summary).join(' '),
    appeal: {
      hint: RESTRICTION_APPEAL_HINT,
      url: BUSINESS_SUPPORT_HOME_URL,
      label: 'Business Support Home',
    },
  };
}

export function hasAccountRestrictions(restrictions: unknown): boolean {
  if (!Array.isArray(restrictions)) return false;
  return restrictions.some((entry) => toNotice(entry, new Date()) !== null);
}
